import { useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { UIToast } from '@/ui';
import { useCompaniesTranslation } from '../hooks';

type UseOpenUpdateSubscriptionPortalActionProps = {
    companyId: string;
};

type UseOpenUpdateSubscriptionPortalActionData = {
    openUpdateSubscriptionPortal: () => Promise<void>;
    loading: boolean;
    error: string;
};

/**
 * Opening the Stripe portal at the page for changing the subscription's plan or seat count.
 *
 * Carries no `isAllowed` of its own: the billing tab is gated as a whole by
 * `canEditBilling` on the billing settings page, and everything on it is
 * reachable only from there.
 */
export function useOpenUpdateSubscriptionPortalAction(
    props: UseOpenUpdateSubscriptionPortalActionProps
): UseOpenUpdateSubscriptionPortalActionData {
    const { companyId } = props;

    const { t } = useCompaniesTranslation();
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const mutation = useMutation({
        mutationFn: () => {
            const hostURL = window.location.origin;
            const returnUrl = `${hostURL}/company/${companyId}/settings/billing`;

            return openApiRef.current.companies
                .createStripeUpdatePortalSession(companyId, { returnUrl })
                .then((response) => {
                    // Stripe hosts the portal, so the browser leaves the app
                    // here. Nothing to write to the cache on the way out.
                    window.location.replace(response.data.data.url);
                });
        },
        onError: () => {
            // Deliberately one message for every error this endpoint declares.
            // Whether the refusal was a 403, a missing company or a bad
            // request, the user is left on this page with nothing opened, so
            // no branch would change what they do next.
            UIToast.error(t('actions.open_update_subscription_portal.error'));
        }
    });

    return {
        openUpdateSubscriptionPortal: () => mutation.mutateAsync(),
        loading: mutation.isPending,
        error: mutation.error
            ? t('actions.open_update_subscription_portal.error')
            : ''
    };
}
